import {inject, Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, firstValueFrom, Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {AuthService} from '@core/authentication/auth.service';
import {ConversationApiService} from '@shared/services/api/conversation/conversation-api.service';
import {
  IUserGoogleAccount,
  IUserNotificationChannelSetting,
  IUserSetting,
  IUserSettingsParsed,
  IUserSettingsResponse,
} from '@shared/models/user-setting.model';

/**
 * Service quản lý tập trung TOÀN BỘ cài đặt người dùng từ API /user/setting.
 *
 * Trách nhiệm:
 * - Gọi API /user/setting MỘT LẦN khi vào project (lazy, first-access)
 * - Parse và cache toàn bộ cấu trúc `IUserSetting`
 * - Cung cấp getter đồng bộ cho từng nhóm cài đặt
 * - Dùng chung toàn bộ project — không chỉ module hội thoại
 *
 * FE13 parity: thay thế các lần gọi rải rác db.userSetting() / getSetting()
 * bằng một service tập trung duy nhất, không cần gọi API lại.
 *
 * Usage:
 * const uss = inject(UserSettingsService);
 * await uss.loadSettings(); // gọi 1 lần ở app init / layout init
 * const sort = uss.parsed().sortActivities;
 * const hideAd = uss.parsed().hideAdIcon;
 */
@Injectable({providedIn: 'root'})
export class UserSettingsService implements OnDestroy {
  private readonly api = inject(ConversationApiService);
  private readonly auth = inject(AuthService);

  /** Subject lưu raw response — null = chưa load */
  private readonly _raw$ = new BehaviorSubject<IUserSettingsResponse | null>(null);

  /** Subject lưu parsed setting — null = chưa load */
  private readonly _parsed$ = new BehaviorSubject<IUserSettingsParsed | null>(null);

  /** Phát lại setting hiện hành để các feature đang mở nhận thay đổi ngay. */
  public readonly settings$ = this._parsed$.asObservable();

  /** Observable phát `true` khi settings đã load xong. */
  public readonly loaded$: Observable<boolean> = new Observable(observer => {
    if (this._parsed$.getValue() !== null) {
      observer.next(true);
      observer.complete();
      return;
    }
    const sub = this._parsed$.subscribe(v => {
      if (v !== null) {
        observer.next(true);
        observer.complete();
      }
    });
    return () => sub.unsubscribe();
  });

  /** Đã gọi API chưa (tránh gọi lại). */
  private _loaded = false;
  /** Promise lock chống concurrent calls. */
  private _loadingPromise: Promise<IUserSettingsParsed> | null = null;

  private readonly _destroy$ = new Subject<void>();

  constructor() {
    // Khi auth thay đổi (chọn page, đổi business) → reset cache + tải lại settings
    this.auth.onAuthChanged$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.resetState();
      if (this.auth.isLoginEd()) void this.loadSettings();
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  // ── Default values ──────────────────────────────────────────────────────

  private static readonly DEFAULTS: IUserSettingsParsed = {
    display: 100,
    sortActivities: 1,
    hideAdIcon: false,
    showNameStaff: false,
    hideReadAvatar: false,
    enter2next: false,
    sendPhotoModes: '1',
    timeFormatType: 1,
    searchQmsgMatch: {shortcut: false, title: false, message: false},
    pasteOptions: {type: 2, defaultValue: null},
    notifications: {on: 1, sound: 'default'},
    notifications_sounds: {
      message: {on: 2, sound: 'default'},
      comment: {on: 2, sound: 'default'},
    },
    raw: {} as IUserSetting,
  };

  // ── Public API ──────────────────────────────────────────────────────────

  /**
   * Load settings từ server. Gọi 1 lần duy nhất — các lần sau trả về cache.
   * Thread-safe: nếu đang load thì join promise hiện tại.
   *
   * API v2: POST /user/setting/index → parse res.data.setting
   */
  public async loadSettings(): Promise<IUserSettingsParsed> {
    if (this._loaded) {
      return this._parsed$.getValue() ?? UserSettingsService.DEFAULTS;
    }

    if (this._loadingPromise) {
      return this._loadingPromise;
    }

    this._loadingPromise = this._doLoad();
    return this._loadingPromise;
  }

  /**
   * Trả về parsed settings (đồng bộ).
   * Nếu chưa load → trả về defaults.
   */
  public parsed(): IUserSettingsParsed {
    return this._parsed$.getValue() ?? UserSettingsService.DEFAULTS;
  }

  /**
   * Trả về raw setting gốc (đồng bộ).
   * Dùng khi cần đọc các field không có trong IUserSettingsParsed.
   */
  public raw(): IUserSettingsResponse | null {
    return this._raw$.getValue();
  }

  /**
   * Trả về raw `setting` object bên trong response.
   */
  public setting(): IUserSetting | null {
    return this._raw$.getValue()?.setting ?? null;
  }

  public googleAccount(): IUserGoogleAccount | null {
    return this._raw$.getValue()?.googleAccount ?? null;
  }

  /**
   * Ghi một phần cài đặt và phát state mới cho các màn hình đang mở.
   * API v2: `POST /user/setting/update` với `setting` JSON.
   */
  public async updateSettings(patch: Partial<IUserSetting>): Promise<boolean> {
    const current = this.setting() ?? {} as IUserSetting;
    const setting = {
      ...current,
      ...patch,
      ...(patch.notifications ? {
        notifications: {...current.notifications, ...patch.notifications},
      } : {}),
      ...(patch.notifications_sounds ? {
        notifications_sounds: {
          message: {...current.notifications_sounds?.message, ...patch.notifications_sounds.message},
          comment: {...current.notifications_sounds?.comment, ...patch.notifications_sounds.comment},
        },
      } : {}),
      ...(patch.pasteOptions ? {
        pasteOptions: {...current.pasteOptions, ...patch.pasteOptions},
      } : {}),
      ...(patch.searchQmsgMatch ? {
        searchQmsgMatch: {...current.searchQmsgMatch, ...patch.searchQmsgMatch},
      } : {}),
    } as IUserSetting;
    const response = await firstValueFrom(this.api.updateUserSetting({
      setting: JSON.stringify(setting),
    }));
    if (response?.code !== 1) return false;

    const raw = this._raw$.getValue() ?? {};
    this._raw$.next({...raw, setting});
    this._parsed$.next(this._parse(setting));
    // Giữ auth/localStorage cùng snapshot với state trung tâm. Đây là cần
    // thiết cho các service đã được tạo trước khi màn hình Settings mở ra,
    // ví dụ notification sound khi DO event tới.
    this.auth.updateUserSettings(setting as unknown as Record<string, unknown>);
    return true;
  }

  // ── Convenience getters (sync, no null checks needed) ──────────────────

  /** Kiểu sắp xếp hội thoại: 1=unread first, 2=by time, 3=no reply first */
  public sortActivities(): number {
    return this.parsed().sortActivities;
  }

  /** Ẩn icon quảng cáo */
  public hideAdIcon(): boolean {
    return this.parsed().hideAdIcon;
  }

  /** Hiển thị tên nhân viên ở footer */
  public showNameStaff(): boolean {
    return this.parsed().showNameStaff;
  }

  /** Ẩn avatar đã đọc */
  public hideReadAvatar(): boolean {
    return this.parsed().hideReadAvatar;
  }

  /** Enter để gửi + chuyển tiếp */
  public enter2next(): boolean {
    return this.parsed().enter2next;
  }

  /** Chế độ gửi ảnh: '1'=gộp, '2'=tách */
  public sendPhotoModes(): string {
    return this.parsed().sendPhotoModes;
  }

  /** Định dạng thời gian: 1=tương đối, 2=ngày giờ */
  public timeFormatType(): number {
    return this.parsed().timeFormatType;
  }

  /** Cài đặt tìm kiếm tin nhắn nhanh */
  public searchQmsgMatch(): IUserSettingsParsed['searchQmsgMatch'] {
    return this.parsed().searchQmsgMatch;
  }

  /** Cài đặt paste */
  public pasteOptions(): IUserSettingsParsed['pasteOptions'] {
    return this.parsed().pasteOptions;
  }

  /** Cài đặt thông báo */
  public notifications(): IUserSettingsParsed['notifications'] {
    return this.parsed().notifications;
  }

  /** Số item hiển thị mặc định (limit) — từ raw setting display */
  public displayLimit(): number {
    const raw = this.setting();
    if (raw?.display && typeof raw.display === 'number') return raw.display;
    return 100;
  }

  /** Reset state khi logout / đổi business. */
  public resetState(): void {
    this._loaded = false;
    this._loadingPromise = null;
    this._raw$.next(null);
    this._parsed$.next(null);
  }

  // ── Private helpers ───────────────────────────────────────────────────

  private async _doLoad(): Promise<IUserSettingsParsed> {
    try {
      const res: any = await firstValueFrom(this.api.getUserSetting());
      // V2 lỗi (ApiService.handleError trả code 0, data rỗng): không parse thành mặc định.
      if (res?.code !== undefined && res.code !== 1) {
        return this._loadFallback();
      }
      // Hỗ trợ cả V1 ({setting:...}) và V2 ({data:{setting:...}})
      const raw: IUserSettingsResponse = res?.code !== undefined
        ? (res.data ?? res)
        : res;
      this._raw$.next(raw);

      const s = raw?.setting ?? {};
      const parsed = this._parse(s);
      this._parsed$.next(parsed);
      this._loaded = true;
      return parsed;
    } catch {
      // FE13 pattern: không toast
      return this._loadFallback();
    } finally {
      this._loadingPromise = null;
    }
  }

  /**
   * API lỗi: phát cài đặt đã lưu trong auth (mặc định nếu chưa có) để màn hình đang chờ
   * loaded$ chạy tiếp. Mặc định là "Chưa đọc lên đầu", dùng nó sẽ sai thứ tự hội thoại
   * của người đã chọn "Mới nhất". Không đánh dấu đã tải để lần gọi sau thử lại API.
   */
  private _loadFallback(): IUserSettingsParsed {
    const parsed = this._parse(this.auth.getSettings() ?? {});
    this._parsed$.next(parsed);
    return parsed;
  }

  private _parse(s: Record<string, unknown>): IUserSettingsParsed {
    return {
      display: this._toNumber(s['display'], 100),
      sortActivities: this._toNumber(s['sortActivities'], 1),
      hideAdIcon: this._toBool(s['hideAdIcon']),
      showNameStaff: this._toNumber(s['hideHandleName'], 1) === 2,
      hideReadAvatar: this._toBool(s['hideReadAvatar']),
      enter2next: this._toBool(s['enter2next']),
      sendPhotoModes: this._toString(s['sendPhotoModes'], '1'),
      timeFormatType: this._toNumber(s['timeFormatType'], 1),
      searchQmsgMatch: this._parseSearchQmsgMatch(s['searchQmsgMatch']),
      pasteOptions: this._parsePasteOptions(s['pasteOptions']),
      notifications: this._parseNotification(s['notifications']),
      notifications_sounds: this._parseNotificationSounds(s['notifications_sounds']),
      raw: s as unknown as IUserSetting,
    };
  }

  private _parseNotificationSounds(raw: unknown): IUserSettingsParsed['notifications_sounds'] {
    const value = raw && typeof raw === 'object' && !Array.isArray(raw)
      ? raw as Record<string, unknown>
      : {};

    return {
      message: this._parseNotificationChannel(value['message']),
      comment: this._parseNotificationChannel(value['comment']),
    };
  }

  private _parseNotificationChannel(raw: unknown): IUserNotificationChannelSetting {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const value = raw as Record<string, unknown>;
      return {
        on: this._toNumber(value['on'], 2),
        sound: this._toString(value['sound'], 'default'),
      };
    }
    return {on: 2, sound: 'default'};
  }

  private _parseNotification(raw: unknown): IUserSettingsParsed['notifications'] {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const obj = raw as Record<string, unknown>;
      return {
        on: this._toNumber(obj['on'], 1),
        sound: this._toString(obj['sound'], 'default'),
      };
    }
    return {on: 1, sound: 'default'};
  }

  private _parseSearchQmsgMatch(raw: unknown): IUserSettingsParsed['searchQmsgMatch'] {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const obj = raw as Record<string, unknown>;
      return {
        shortcut: this._toBool(obj['shortcut']),
        title: this._toBool(obj['title']),
        message: this._toBool(obj['message']),
      };
    }
    return {shortcut: false, title: false, message: false};
  }

  private _parsePasteOptions(raw: unknown): IUserSettingsParsed['pasteOptions'] {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const obj = raw as Record<string, unknown>;
      return {
        type: this._toNumber(obj['type'], 2),
        defaultValue: obj['defaultValue'] ?? null,
      };
    }
    return {type: 2, defaultValue: null};
  }

  private _toNumber(val: unknown, fallback: number): number {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
      const n = Number(val);
      return isNaN(n) ? fallback : n;
    }
    return fallback;
  }

  /** FE13: setting values use 1=true/bật, 2=false/tắt */
  private _toBool(val: unknown): boolean {
    if (typeof val === 'boolean') return val;
    if (typeof val === 'number') return val === 1;
    if (typeof val === 'string') return val === '1' || val === 'true';
    return false;
  }

  private _toString(val: unknown, fallback: string): string {
    if (typeof val === 'string') return val;
    if (typeof val === 'number') return String(val);
    return fallback;
  }
}
